import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FadeIn from "@/components/FadeIn";
import SEO from "@/components/SEO";
import axios from "axios";
import { Calendar, User, ArrowLeft, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="section-padding bg-[#F9F9F8]">
        <div className="max-w-3xl mx-auto animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-1/4 mb-4" />
          <div className="h-12 bg-slate-200 rounded w-3/4 mb-6" />
          <div className="h-64 bg-slate-200 rounded-2xl mb-8" />
          <div className="space-y-3">
            <div className="h-4 bg-slate-200 rounded" />
            <div className="h-4 bg-slate-200 rounded w-5/6" />
            <div className="h-4 bg-slate-200 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="section-padding bg-[#F9F9F8] text-center" data-testid="blog-not-found">
        <h2 className="font-outfit text-2xl text-[#03045e] mb-4">Post Not Found</h2>
        <Link to="/blog">
          <Button variant="outline" className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      <SEO title={post.title} description={post.excerpt || post.title} path={`/blog/${post.slug}`} type="article" />
      <section className="section-padding bg-[#F9F9F8]">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <Link to="/blog" className="inline-flex items-center text-sm text-[#00A4A9] hover:underline mb-6" data-testid="back-to-blog">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Blog
            </Link>

            <div className="flex items-center gap-3 mb-4">
              <Badge variant="secondary" className="bg-[#e0f2fe] text-[#00A4A9]">{post.category}</Badge>
              <span className="text-sm text-slate-400 flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </span>
              <span className="text-sm text-slate-400 flex items-center gap-1">
                <User className="w-3.5 h-3.5" /> {post.author}
              </span>
            </div>

            <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-medium text-[#03045e] mb-6 tracking-tight leading-tight">
              {post.title}
            </h1>

            {post.image_url && (
              <img src={post.image_url} alt={post.title} className="w-full rounded-2xl mb-8 max-h-96 object-cover" />
            )}

            <div
              className="prose prose-slate max-w-none text-slate-600 leading-relaxed [&_h2]:font-outfit [&_h2]:text-[#03045e] [&_h3]:font-outfit [&_h3]:text-[#03045e] [&_a]:text-[#00A4A9] [&_blockquote]:border-l-[#00A4A9] [&_img]:rounded-xl"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="blog-post-content"
            />

            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="font-outfit text-lg font-medium text-[#03045e] mb-4">Ready to start your medical journey?</h3>
              <Link to="/contact" data-testid="blog-cta">
                <Button className="rounded-full bg-[#00A4A9] hover:bg-[#008084] text-white">
                  Get Free Consultation <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
